import { useState, useEffect } from 'react';
import './FeedbackModal.css';

const FEEDBACK_TYPES = [
  { value: 'Verbesserungsvorschlag', icon: '💡' },
  { value: 'Fehler melden',          icon: '🐛' },
  { value: 'Feature-Wunsch',         icon: '✨' },
  { value: 'Allgemeines Feedback',   icon: '💬' },
  { value: 'Lob',                    icon: '👍' },
];

const MAX_IMAGES = 5;
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];

export default function FeedbackModal({ isOpen, onClose, onSubmit }) {
  const [feedbackType, setFeedbackType] = useState('Verbesserungsvorschlag');
  const [message, setMessage] = useState('');
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      images.forEach(({ previewUrl }) => URL.revokeObjectURL(previewUrl));
      setImages([]);
      setFeedbackType('Verbesserungsvorschlag');
      setMessage('');
      setError('');
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const addImageFiles = (files) => {
    const accepted = [];
    let errorMsg = '';

    for (const file of files) {
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        errorMsg = 'Nur Bilddateien (JPEG, PNG, WebP, GIF) sind erlaubt.';
        continue;
      }
      if (file.size > MAX_IMAGE_SIZE) {
        errorMsg = 'Bilder dürfen maximal 10 MB groß sein.';
        continue;
      }
      if (images.length + accepted.length >= MAX_IMAGES) {
        errorMsg = `Maximal ${MAX_IMAGES} Bilder erlaubt.`;
        break;
      }
      accepted.push({
        id: Date.now() + Math.random(),
        file,
        previewUrl: URL.createObjectURL(file),
      });
    }

    setError(errorMsg);
    if (accepted.length > 0) {
      setImages(prev => [...prev, ...accepted]);
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length > 0) addImageFiles(files);
  };

  const handlePaste = (e) => {
    const files = Array.from(e.clipboardData?.items || [])
      .filter(item => item.kind === 'file' && item.type.startsWith('image/'))
      .map(item => item.getAsFile())
      .filter(Boolean);
    if (files.length > 0) {
      e.preventDefault();
      addImageFiles(files);
    }
  };

  const removeImage = (id) => {
    setImages(prev => {
      const item = prev.find(img => img.id === id);
      if (item) URL.revokeObjectURL(item.previewUrl);
      return prev.filter(img => img.id !== id);
    });
  };

  const resetForm = () => {
    images.forEach(({ previewUrl }) => URL.revokeObjectURL(previewUrl));
    setImages([]);
    setFeedbackType('Verbesserungsvorschlag');
    setMessage('');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 10) {
      setError('Bitte gib mindestens 10 Zeichen ein.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await onSubmit(feedbackType, message, images.map(({ file }) => file));
      resetForm();
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) onClose();
  };

  return (
    <div className="fb-overlay" onClick={handleClose}>
      <div className="fb-modal" onClick={(e) => e.stopPropagation()} onPaste={handlePaste}>

        {/* Header */}
        <div className="fb-header">
          <div className="fb-header-left">
            <span className="fb-header-icon">💬</span>
            <div>
              <h2>Feedback senden</h2>
              <p>Hilf uns, die App zu verbessern</p>
            </div>
          </div>
          <button className="fb-close" onClick={handleClose} disabled={loading}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="fb-body">

            {/* Info note */}
            <div className="fb-info-note">
              <span className="fb-info-icon">🔒</span>
              <span>Bitte sende keine sensiblen Informationen oder persönliche Daten in deinem Feedback. Deine Nachricht wird direkt an den Support gesendet.</span>
            </div>

            {/* Type chips */}
            <div>
              <div className="fb-label">Feedback-Typ</div>
              <div className="fb-type-grid">
                {FEEDBACK_TYPES.map(({ value, icon }) => (
                  <button
                    key={value}
                    type="button"
                    className={`fb-type-chip${feedbackType === value ? ' selected' : ''}`}
                    onClick={() => setFeedbackType(value)}
                    disabled={loading}
                  >
                    <span className="fb-type-chip-icon">{icon}</span>
                    {value}
                  </button>
                ))}
              </div>
            </div>

            {/* Message */}
            <div>
              <div className="fb-label">Deine Nachricht</div>
              <div className="fb-textarea-wrap">
                <textarea
                  className={`fb-textarea${error && message.trim().length < 10 ? ' has-error' : ''}`}
                  value={message}
                  onChange={(e) => { setMessage(e.target.value); if (e.target.value.trim().length >= 10) setError(''); }}
                  placeholder="Beschreibe dein Anliegen so detailliert wie möglich…"
                  required
                  minLength={10}
                  rows={6}
                  autoFocus
                  disabled={loading}
                />
                <div className="fb-textarea-footer">
                  {error
                    ? <span className="fb-error-msg">{error}</span>
                    : <span />
                  }
                  <span className="fb-char-count">{message.length} Zeichen</span>
                </div>
              </div>
            </div>

            {/* Images */}
            <div>
              <div className="fb-label">Bilder anhängen (optional)</div>
              <div className="fb-images">
                {images.map(({ id, previewUrl, file }) => (
                  <div key={id} className="fb-image-item">
                    <img src={previewUrl} alt={file.name} className="fb-image-preview" />
                    <button
                      type="button"
                      className="fb-image-remove"
                      onClick={() => removeImage(id)}
                      disabled={loading}
                      title="Bild entfernen"
                    >
                      ✕
                    </button>
                    <span className="fb-image-name">{file.name}</span>
                  </div>
                ))}
                {images.length < MAX_IMAGES && (
                  <label className="fb-image-add">
                    <span className="fb-image-add-icon">📷</span>
                    <span>Bild hinzufügen</span>
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                      multiple
                      className="fb-image-input"
                      onChange={handleImageSelect}
                      disabled={loading}
                    />
                  </label>
                )}
              </div>
              <p className="fb-images-hint">
                Max. {MAX_IMAGES} Bilder, je 10 MB (JPEG, PNG, WebP, GIF) – Screenshots können auch direkt eingefügt werden (Strg+V)
              </p>
            </div>

          </div>

          {/* Footer */}
          <div className="fb-footer">
            <button type="button" className="fb-cancel-btn" onClick={handleClose} disabled={loading}>
              Abbrechen
            </button>
            <button type="submit" className="fb-submit-btn" disabled={loading}>
              {loading ? '⏳ Wird gesendet…' : '📤 Feedback senden'}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}
